import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { InfoBanner } from 'ui/banner';
import { BUTTON_VARIANTS, Button } from 'ui/button';
import { Help16, Open16 } from 'ui/icons';
import { TipPlacement } from 'ui/tooltip';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { DisplayPriorityCampaign } from 'modules/placements/types';
import { useDisplayPriorityContext } from 'modules/placements/context/DisplayPriorityContext';
import {
  FooterWrapper,
  StyledTakeover,
  TakeoverHeaderBarContent,
} from 'modules/campaigns/editor/takeovers/Takeovers.styled';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import { DisplayPriorityEdit } from './DisplayPriorityEdit';
import {
  GlobalBodyStyles,
  BodyHeader,
  BodyHeaderContainer,
  BodyWrapper,
  CampaignColumnsGrid,
  CampaignTitle,
  Description,
  DescriptionContainer,
  DisplayUntil,
  DividedList,
  DividedListElement,
  Eligible,
  PlacementPreview,
  PlacementPreviewTitleCell,
  StartDate,
  TakeoverGrid,
  Title,
  PlacementTitle,
  StyledCampaignLimitInfo,
  CampaignLimit,
  StyledTooltip,
  ScrollableContainer,
  StyledTextButton,
} from './styled/DisplayPriorityTakeover.styled';
import { ConfirmUpdateDisplayPriorityModal } from './ConfirmUpdateDisplayPriorityModal';
import { PreviewCarousel } from './PreviewCarousel';

// currentCampaignId is an optional value specific to the campaign editor view of the takeover that will mark the campaign
// that is being edited in the list as the current campaign
type DisplayPriorityTakeoverProps = {
  onClose: () => void;
  currentCampaignId?: number;
  isOverlay?: boolean;
};

export const DisplayPriorityTakeover = ({
  onClose,
  currentCampaignId,
  isOverlay = true,
}: DisplayPriorityTakeoverProps) => {
  const navBarHeight = useNavBarHeight();
  const [scrollPosition, setScrollPosition] = useState<number>(0);

  const {
    campaignDisplayPriorityOrder,
    campaignPlacement,
    campaignsByPlacement,
    isDirty,
    setCampaignDisplayPriorityOrder,
    saveDisplayPriority,
    isLoadingCampaignsForPlacement,
  } = useDisplayPriorityContext();

  const canViewPlacements = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.ViewChannelConfiguration,
  );

  const [isConfirmModalShowing, setIsConfirmModalShowing] = useState<boolean>(false);
  const [isInfoBannerShowing, setIsInfoBannerShowing] = useState<boolean>(
    campaignsByPlacement?.length >= 20,
  );

  const initialDisplayPriority = useRef<DisplayPriorityCampaign[]>([]);
  const resetInitialDisplayPriority = () => {
    initialDisplayPriority.current = campaignDisplayPriorityOrder;
  };

  const handleClose = () => {
    onClose();
    resetInitialDisplayPriority();
  };

  const handleDiscard = () => {
    onClose();
    setCampaignDisplayPriorityOrder(initialDisplayPriority.current);
    setIsConfirmModalShowing(false);
  };

  const handleSubmit = () => {
    saveDisplayPriority();
    setIsConfirmModalShowing(false);
    handleClose();
  };

  const handleConfirm = () => {
    setIsConfirmModalShowing(true);
  };

  const handleScrollPosition = (e: SyntheticEvent) => {
    const target = e.target as HTMLDivElement;
    setScrollPosition(target.scrollTop);
  };

  useEffect(() => {
    if (!initialDisplayPriority.current.length) resetInitialDisplayPriority();
  }, [campaignDisplayPriorityOrder]);

  return (
    <>
      <StyledTakeover
        size="100%"
        isOverlay={isOverlay}
        onClose={isDirty ? handleConfirm : handleClose}
        HeaderBarContent={
          <TakeoverHeaderBarContent>Campaign display priority order</TakeoverHeaderBarContent>
        }
        noPadding={true}
        Body={
          <BodyWrapper>
            {isInfoBannerShowing && (
              <InfoBanner onClose={() => setIsInfoBannerShowing(false)}>
                Having trouble sorting a large list of campaigns? Try using the arrow keys, or
                archiving old campaigns.
              </InfoBanner>
            )}
            <BodyHeaderContainer data-scrolly={scrollPosition}>
              <BodyHeader>
                <Title data-test="placement-title">{campaignPlacement?.placementName}</Title>
                {canViewPlacements && (
                  <StyledTextButton
                    aria-label={'Manage embedded placements in a new window'}
                    onClick={() =>
                      window.open(
                        `/settings/placements?placementId=${campaignPlacement?.id}`,
                        '_blank',
                      )
                    }
                  >
                    View placement
                    <Open16 />
                  </StyledTextButton>
                )}
              </BodyHeader>
              <DescriptionContainer>
                <DividedList>
                  <DividedListElement>{`Placement ID: ${campaignPlacement?.id}`}</DividedListElement>
                  {campaignPlacement?.description && (
                    <DividedListElement>{campaignPlacement.description}</DividedListElement>
                  )}
                </DividedList>
                {scrollPosition === 0 && (
                  <Description>
                    Determine the campaigns shown to an eligible customer when multiple campaigns
                    are active. Drag campaigns to set the display priority for the placement.
                  </Description>
                )}
              </DescriptionContainer>
            </BodyHeaderContainer>
            <TakeoverGrid data-scrolly={scrollPosition} navBarHeight={navBarHeight}>
              <ScrollableContainer onScroll={handleScrollPosition}>
                <CampaignColumnsGrid data-test="campaign-section-columns-grid">
                  <CampaignTitle>Campaign name</CampaignTitle>
                  <Eligible> Who&apos;s eligible</Eligible>
                  <StartDate>Start date</StartDate>
                  <DisplayUntil>Display until</DisplayUntil>
                </CampaignColumnsGrid>
                <DisplayPriorityEdit currentCampaignId={currentCampaignId} />
              </ScrollableContainer>
              <PlacementPreviewTitleCell data-test="placement-preview-title-cell">
                <PlacementTitle>Placement preview</PlacementTitle>
                <StyledCampaignLimitInfo>
                  <CampaignLimit>{`${campaignPlacement?.campaignLimit} campaign limit`}</CampaignLimit>
                  <StyledTooltip
                    tooltipContent={`Up to ${campaignPlacement?.campaignLimit} eligible campaigns will be visible to customers at once in the Home page carousel placement.`}
                    placement={TipPlacement.Left}
                  >
                    <Help16 />
                  </StyledTooltip>
                </StyledCampaignLimitInfo>
              </PlacementPreviewTitleCell>
              <PlacementPreview>
                <PreviewCarousel />
              </PlacementPreview>
            </TakeoverGrid>
          </BodyWrapper>
        }
        Footer={
          <FooterWrapper>
            <Button
              data-test="display-priority-takeover-submit-button"
              isDisabled={isLoadingCampaignsForPlacement}
              onClick={handleSubmit}
              variant={BUTTON_VARIANTS.PRIMARY}
            >
              Save
            </Button>
          </FooterWrapper>
        }
      />
      {isConfirmModalShowing && (
        <ConfirmUpdateDisplayPriorityModal
          onConfirm={handleSubmit}
          onDiscard={handleDiscard}
          onClose={() => setIsConfirmModalShowing(false)}
        />
      )}
      <GlobalBodyStyles />
    </>
  );
};
